class SiblingProductSwitcher extends HTMLElement {
  constructor() {
    super();
    this.inputs = [];
    this.sectionId = this.getAttribute('data-section-id');
    this.productCache = new Map();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('input[name="sibling-product"]'));
    this.attachEvents();
    // Preload siblings when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.preloadSiblings());
    } else {
      setTimeout(() => this.preloadSiblings(), 300);
    }
  }

  disconnectedCallback() {
    this.detachEvents();
  }

  attachEvents() {
    this.inputs.forEach((input) => input.addEventListener('change', this.onChange));
  }

  detachEvents() {
    this.inputs.forEach((input) => input.removeEventListener('change', this.onChange));
  }

  async onChange(event) {
    const target = event.target;

    this.inputs.forEach((input) => {
      input.checked = false;
    });

    target.checked = true;

    if (!target?.value) return;

    const handle = target.value;
    const productId = target.dataset.productId;

    try {
      const newSection = await this.getSectionForHandle(handle);
      if (!newSection) return;

      const newSectionClone = newSection.cloneNode(true);

      const currentSection = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!currentSection) return;

      this.preserveNonReplacable(currentSection, newSectionClone);
      this.preserveKlarnaWidget(currentSection, newSectionClone);

      // Add class to elements that require adding a class on fetch
      const classOnFetchElements = newSectionClone.querySelectorAll('[data-class-on-fetch]');
      classOnFetchElements.forEach((element) => {
        element.classList.add(element.dataset.classOnFetch);
      });

      currentSection.replaceWith(newSectionClone);

      // Reinit lazy images for a newly inserted section
      newSectionClone.querySelectorAll('img.js-lazy[data-src], img.js-lazy[data-srcset]').forEach((img) => {
        if (img.dataset.srcset) { img.srcset = img.dataset.srcset; delete img.dataset.srcset; }
        if (img.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
        img.classList.remove('js-lazy');
      });

      // Reinit lazy videos for a newly inserted section
      newSectionClone.querySelectorAll('video.js-lazy').forEach((video) => {
        video.querySelectorAll('source[data-src]').forEach(source => {
          source.src = source.dataset.src;
          delete source.dataset.src;
        });
        video.load();
        video.classList.remove('js-lazy');
        video.play().catch(() => {});
      });

      // Re-init magnify on a new section
      if (window.jQuery && typeof window.jQuery.fn.magnify === 'function') {
        window.jQuery(newSectionClone).find('.zoomHolder').magnify();
      }

      // Update URL (preserve query params unrelated to product)
      const url = new URL(window.location.href);
      url.pathname = `/products/${handle}`;
      url.search = '';
      window.history.replaceState({ path: url.toString() }, '', url.toString());

      // Announce color change to screen readers
      const colorName = target.getAttribute('aria-label');
      if (colorName) {
        this.announceToScreenReader(`Selected ${colorName}`);
      }

      // Announce change if anyone needs to react
      window.dispatchEvent(new CustomEvent('url:changed', { detail: { productId } }));
    } catch (e) {
      console.error('[SiblingProductSwitcher] Failed to switch product', e);
    }
  }

  async getSectionForHandle(handle) {
    let htmlText = this.productCache.get(handle);

    if (htmlText === undefined) {
      const response = await fetch(`/products/${handle}?section_id=${this.sectionId}`);
      if (!response.ok) return null;
      htmlText = await response.text();
      // Cache raw HTML string, not the DOM node
      this.productCache.set(handle, htmlText);
    }

    // Parse fresh DOM from cached HTML string each time
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    return doc.getElementById(`MainProduct-${this.sectionId}`);
  }

  preloadSiblings() {
    this.inputs.forEach((input) => {
      const handle = input.value;
      if (!this.productCache.has(handle)) {
        this.getSectionForHandle(handle).catch(() => { });
      }
    });
  }

  preserveNonReplacable(currentSection, incomingSection) {
    try {
      const currentList = Array.from(
        currentSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );
      if (!currentList.length) return;

      const incomingList = Array.from(
        incomingSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );

      const count = Math.min(currentList.length, incomingList.length);
      for (let i = 0; i < count; i++) {
        const keepNode = currentList[i];
        const replaceTarget = incomingList[i];
        if (!keepNode || !replaceTarget) continue;
        replaceTarget.replaceWith(keepNode);
      }
    } catch (err) {
      console.warn('[SiblingProductSwitcher] preserveNonReplacable failed', err);
    }
  }

  preserveKlarnaWidget(currentSection, incomingSection) {
    try {
      const currentKlarnaWidget = currentSection.querySelector('klarna-placement');
      if (!currentKlarnaWidget) return;
      const incomingKlarnaWidget = incomingSection.querySelector('klarna-placement');
      if (!incomingKlarnaWidget) return;
      incomingKlarnaWidget.replaceWith(currentKlarnaWidget);
    } catch (err) {
      console.warn('[SiblingProductSwitcher] preserveKlarnaWidget failed', err);
    }
  }

  announceToScreenReader(message) {
    let announcer = document.getElementById('sibling-switcher-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'sibling-switcher-announcer';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'visually-hidden';
      document.body.appendChild(announcer);
    }
    // Clear and set message to trigger announcement
    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }
}

if (!customElements.get('sibling-product-switcher')) {
  customElements.define('sibling-product-switcher', SiblingProductSwitcher);
}
